import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { View, Text } from '../lib/react-native-compat';
import {
    ROW_H, HEAD_H, barOf, ganttDomain, monthTicks,
} from './weeklyGanttData';

// The Weekly Update grid, drawn.
//
// One track per grid row, same order and same height, so the bar beside a row
// IS that row — change a date in the table and the bar moves with it. That is
// the whole point: the grid is where the plan is edited and this is where a
// PM sees whether the plan they just typed makes sense as a sequence.
//
// Deliberately fitted to its column rather than scrollable: the value here is
// the shape of the job at a glance, and a strip you have to scroll sideways to
// read stops answering "does this look right" the moment it needs two screens.

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

const fmt = (v) => (Number.isFinite(Number(v)) ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—');

const shortDate = (d) => (d ? moment(d).format('M/D/YY') : '—');

const WeeklyGantt = ({ rows, toDate, weekEnding }) => {
    const [width, setWidth] = useState(320);
    const paneRef = useRef(null);

    useEffect(() => {
        const el = paneRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => setWidth(Math.max(el.clientWidth || 320, 160)));
        ro.observe(el);
        setWidth(Math.max(el.clientWidth || 320, 160));
        return () => ro.disconnect();
    }, []);

    const today = moment().format('YYYY-MM-DD');
    const domain = useMemo(() => ganttDomainSafe(rows, today), [rows, today]);
    const months = useMemo(() => monthTicks(domain), [domain]);

    const pxFor = useMemo(() => {
        if (!domain) return () => 0;
        const perDay = width / domain.days;
        const origin = moment(domain.start);
        return (d) => moment(d).diff(origin, 'days') * perDay;
    }, [domain, width]);

    const bars = useMemo(() => (rows || []).map(barOf), [rows]);
    const planned = bars.filter(Boolean).length;
    const todayX = domain && today >= domain.start && today <= domain.end ? pxFor(today) : null;
    const weekX = domain && weekEnding && weekEnding >= domain.start && weekEnding <= domain.end
        ? pxFor(weekEnding)
        : null;

    return (
        <View style={{ minWidth: 0 }}>
            {/* The axis block is exactly the grid's header height, and the title
                lives inside it, so the first track lines up with the first row. */}
            <div ref={paneRef} style={{ width: '100%' }}>
                {!domain ? (
                    <View style={{ height: HEAD_H + ROW_H, justifyContent: 'center' }}>
                        <Text style={{ fontSize: 11, color: '#94A3B8' }}>
                            Give a phase a start date to draw it here.
                        </Text>
                    </View>
                ) : (
                    <View style={{ position: 'relative', width: '100%' }}>
                        {/* Axis */}
                        <View style={{ position: 'relative', height: HEAD_H, width: '100%', overflow: 'hidden' }}>
                            <Text style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                fontSize: 10,
                                fontWeight: '700',
                                color: '#94A3B8',
                                textTransform: 'uppercase',
                                letterSpacing: 0.4,
                                whiteSpace: 'nowrap',
                            }}
                            >
                                {`Timeline · ${shortDate(domain.start)} – ${shortDate(domain.end)}`}
                            </Text>
                            {months.map((m) => (
                                <Text
                                    key={`h${m.key}`}
                                    style={{
                                        position: 'absolute',
                                        left: pxFor(m.date) + 2,
                                        bottom: 4,
                                        fontSize: 9,
                                        fontWeight: '700',
                                        color: m.jan ? '#475569' : '#94A3B8',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {m.jan ? `${m.label} ’${m.year}` : m.label}
                                </Text>
                            ))}
                        </View>

                        {/* Tracks */}
                        <View style={{ position: 'relative', width: '100%' }}>
                            {/* gridlines run the full height behind the bars */}
                            {months.map((m) => (
                                <View
                                    key={`g${m.key}`}
                                    style={{
                                        position: 'absolute', left: pxFor(m.date), top: 0, bottom: 0, width: 1,
                                        backgroundColor: m.jan ? '#E2E8F0' : '#F1F5F9',
                                    }}
                                />
                            ))}
                            {weekX != null && (
                                <View style={{
                                    position: 'absolute', left: weekX, top: 0, bottom: 0, width: 1,
                                    backgroundColor: '#93C5FD', zIndex: 2,
                                }}
                                />
                            )}
                            {todayX != null && (
                                <View style={{
                                    position: 'absolute', left: todayX, top: 0, bottom: 0, width: 2,
                                    backgroundColor: '#F59E0B', zIndex: 3,
                                }}
                                />
                            )}

                            {(rows || []).map((r, i) => {
                                const bar = bars[i];
                                const qty = num(r.qty);
                                const done = toDate ? num(toDate[r.costCode]) : 0;
                                const pct = qty > 0 ? Math.max(0, Math.min(100, (done / qty) * 100)) : 0;
                                const x = bar ? pxFor(bar.start) : 0;
                                const w = bar
                                    ? Math.max(pxFor(moment(bar.finish).add(1, 'days').format('YYYY-MM-DD')) - x, 3)
                                    : 0;
                                const late = bar && !bar.invalid && bar.finish < today && pct < 100;
                                const title = bar
                                    ? `${r.costCode}${r.description ? ` · ${r.description}` : ''}\n`
                                        + `${shortDate(bar.start)} – ${shortDate(bar.finish)}`
                                        + `${bar.inferred ? ' (finish from the daily rate)' : ''}\n`
                                        + `${fmt(qty)} ${r.uom || ''}${done ? ` · ${fmt(done)} installed (${Math.round(pct)}%)` : ''}`
                                    : `${r.costCode}${r.description ? ` · ${r.description}` : ''}\nNot planned yet`;
                                return (
                                    <View
                                        key={`${r.costCode}-${i}`}
                                        title={title}
                                        style={{
                                            position: 'relative',
                                            height: ROW_H,
                                            justifyContent: 'center',
                                            borderTop: '1px solid #F8FAFC',
                                        }}
                                    >
                                        {bar ? (
                                            <View style={{
                                                position: 'absolute',
                                                left: x,
                                                top: (ROW_H - 12) / 2,
                                                width: w,
                                                height: 12,
                                                borderRadius: 3,
                                                overflow: 'hidden',
                                                zIndex: 1,
                                                backgroundColor: bar.invalid ? '#FEE2E2' : (late ? '#FEF3C7' : '#DBEAFE'),
                                                border: `1px solid ${bar.invalid ? '#F87171' : (late ? '#FBBF24' : '#93C5FD')}`,
                                                // A finish the daily rate implied is a guess, and reads like one.
                                                borderStyle: bar.inferred ? 'dashed' : 'solid',
                                            }}
                                            >
                                                {pct > 0 && (
                                                    <View style={{
                                                        width: `${pct}%`,
                                                        height: '100%',
                                                        backgroundColor: '#2563EB',
                                                        opacity: 0.55,
                                                    }}
                                                    />
                                                )}
                                            </View>
                                        ) : (
                                            <Text style={{ fontSize: 9, color: '#CBD5E1', paddingLeft: 2 }}>not planned</Text>
                                        )}
                                    </View>
                                );
                            })}
                        </View>
                    </View>
                )}
            </div>

            {domain && (
                <View style={{
                    flexDirection: 'row', alignItems: 'center', columnGap: 10, flexWrap: 'wrap', marginTop: 6,
                }}
                >
                    <Text style={{ fontSize: 10, color: '#94A3B8' }}>{`${planned} of ${(rows || []).length} drawn`}</Text>
                    {todayX != null && <Text style={{ fontSize: 10, color: '#B45309' }}>│ today</Text>}
                    {weekX != null && <Text style={{ fontSize: 10, color: '#60A5FA' }}>│ week ending</Text>}
                </View>
            )}
        </View>
    );
};

// Domain is computed off the live grid rows, which can be mid-edit (a cleared
// date, a half-typed year), so a throw here must not take the page down with
// it — no picture is a better failure than no grid.
function ganttDomainSafe(rows, today) {
    try {
        return ganttDomain(rows, today);
    } catch (e) {
        return null;
    }
}

export default WeeklyGantt;
